import { HttpApiEndpoint, HttpApiGroup } from "@effect/platform";
import * as S from "effect/Schema";
import { PrincipalMiddleware } from "./principal.js";
import { Forbidden } from "./shares.js";
/**
 * Typed SAP and Personio integration intents. Credential storage and provider
 * network calls stay server-side. Client-addressed endpoints scope officers
 * to their own client while administrators may act across clients.
 */
export const ConnectionResult = S.Struct({
    success: S.Boolean,
    message: S.String,
});
const Saved = S.Struct({ saved: S.Literal(true) });
export const UploadResult = S.Struct({
    employees_found: S.Int,
    queued_for_creation: S.Int,
});
export const HrisProvider = S.Literal("sap", "personio");
export const HrisConfiguration = S.Struct({
    sap_enabled: S.Boolean,
    sap_api_url: S.NullOr(S.String),
    personio_enabled: S.Boolean,
    hr_auto_sync_enabled: S.Boolean,
});
export const GetHrisConfigurationParams = S.Struct({
    pima_client_id: S.UUID,
});
export const GetHrisConfiguration = HttpApiEndpoint.get("getHrisConfiguration", "/hris/configuration")
    .setUrlParams(GetHrisConfigurationParams)
    .addSuccess(S.Struct({ data: HrisConfiguration }))
    .addError(Forbidden, { status: 403 });
export const UpdateHrisAutoSyncPayload = S.Struct({
    pima_client_id: S.UUID,
    enabled: S.Boolean,
});
export const UpdateHrisAutoSync = HttpApiEndpoint.post("updateHrisAutoSync", "/hris/automatic-sync")
    .setPayload(UpdateHrisAutoSyncPayload)
    .addSuccess(S.Struct({ data: S.Struct({ saved: S.Literal(true) }) }))
    .addError(Forbidden, { status: 403 });
export const DisconnectHrisProviderPayload = S.Struct({
    pima_client_id: S.UUID,
    provider: HrisProvider,
});
export const DisconnectHrisProvider = HttpApiEndpoint.post("disconnectHrisProvider", "/hris/disconnect")
    .setPayload(DisconnectHrisProviderPayload)
    .addSuccess(S.Struct({ data: S.Struct({ saved: S.Literal(true) }) }))
    .addError(Forbidden, { status: 403 });
/** The client has no configured (or incomplete) SAP/Personio credentials to upload with. */
export const HrisNotConfigured = S.Struct({
    _tag: S.Literal("HrisNotConfigured"),
});
/** The upstream SAP/Personio API was unreachable or errored mid-upload. */
export const HrisUpstreamUnavailable = S.Struct({
    _tag: S.Literal("HrisUpstreamUnavailable"),
});
// ---------------------------------------------------------------------------
// sap.*
// ---------------------------------------------------------------------------
export const TestSapConnectionPayload = S.Struct({
    client_id: S.NonEmptyString,
    client_secret: S.NonEmptyString,
    api_url: S.NonEmptyString,
    company_id: S.optional(S.String),
    scope: S.optional(S.String),
});
/** Connection testing validates the caller's role before contacting SAP. */
export const TestSapConnection = HttpApiEndpoint.post("testSapConnection", "/hris/sap/test")
    .setPayload(TestSapConnectionPayload)
    .addSuccess(S.Struct({ data: ConnectionResult }))
    .addError(Forbidden, { status: 403 });
export const SaveSapCredentialsPayload = S.Struct({
    pima_client_id: S.UUID,
    client_id: S.NonEmptyString,
    client_secret: S.NonEmptyString,
    api_url: S.NonEmptyString,
    company_id: S.optional(S.String),
    scope: S.optional(S.String),
    hr_auto_sync_enabled: S.optional(S.Boolean),
});
export const SaveSapCredentials = HttpApiEndpoint.post("saveSapCredentials", "/hris/sap/credentials")
    .setPayload(SaveSapCredentialsPayload)
    .addSuccess(S.Struct({ data: Saved }))
    .addError(Forbidden, { status: 403 });
export const UploadSapEmployeesPayload = S.Struct({
    pima_client_id: S.UUID,
});
export const UploadSapEmployees = HttpApiEndpoint.post("uploadSapEmployees", "/hris/sap/upload")
    .setPayload(UploadSapEmployeesPayload)
    .addSuccess(S.Struct({ data: UploadResult }))
    .addError(HrisNotConfigured, { status: 409 })
    .addError(HrisUpstreamUnavailable, { status: 503 })
    .addError(Forbidden, { status: 403 });
// ---------------------------------------------------------------------------
// personio.*
// ---------------------------------------------------------------------------
export const TestPersonioConnectionPayload = S.Struct({
    pima_client_id: S.UUID,
    client_id: S.NonEmptyString,
    client_secret: S.NonEmptyString,
});
export const TestPersonioConnection = HttpApiEndpoint.post("testPersonioConnection", "/hris/personio/test")
    .setPayload(TestPersonioConnectionPayload)
    .addSuccess(S.Struct({ data: ConnectionResult }))
    .addError(Forbidden, { status: 403 });
export const SavePersonioCredentialsPayload = S.Struct({
    pima_client_id: S.UUID,
    client_id: S.NonEmptyString,
    client_secret: S.NonEmptyString,
    webhook_secret: S.optional(S.String),
});
export const SavePersonioCredentials = HttpApiEndpoint.post("savePersonioCredentials", "/hris/personio/credentials")
    .setPayload(SavePersonioCredentialsPayload)
    .addSuccess(S.Struct({ data: Saved }))
    .addError(Forbidden, { status: 403 });
export const UploadPersonioEmployeesPayload = S.Struct({
    pima_client_id: S.UUID,
});
export const UploadPersonioEmployees = HttpApiEndpoint.post("uploadPersonioEmployees", "/hris/personio/upload")
    .setPayload(UploadPersonioEmployeesPayload)
    .addSuccess(S.Struct({ data: UploadResult }))
    .addError(HrisNotConfigured, { status: 409 })
    .addError(HrisUpstreamUnavailable, { status: 503 })
    .addError(Forbidden, { status: 403 });
export const hrisGroup = HttpApiGroup.make("hris")
    .add(GetHrisConfiguration)
    .add(UpdateHrisAutoSync)
    .add(DisconnectHrisProvider)
    .add(TestSapConnection)
    .add(SaveSapCredentials)
    .add(UploadSapEmployees)
    .add(TestPersonioConnection)
    .add(SavePersonioCredentials)
    .add(UploadPersonioEmployees)
    .middleware(PrincipalMiddleware);
//# sourceMappingURL=hris.js.map